import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { useAuth } from "../context/AuthContext";
import { allowedSites } from "../lib/sites";
import { permitMeta } from "../lib/permitSchemas";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserPlus, Search, Users, BadgeCheck, CalendarX, FileCheck2, Building2, List, MapPin } from "lucide-react";
function fmtDate(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dt;
  }
}
const isExpired = (dt) => dt && new Date(dt) < new Date();
const ALL_SITES = "__all__";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ contractors: 0, inducted: 0, induction_expired: 0, permits_active: 0 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [grouped, setGrouped] = useState(false);
  const [filter, setFilter] = useState("all"); // all | inducted | expired | active_permits
  const [site, setSite] = useState(ALL_SITES);

  const sites = allowedSites(user);
  const showSiteFilter = sites.length > 1;

  const load = async (q = "", siteVal = site) => {
    setLoading(true);
    const params = {};
    if (q) params.search = q;
    if (siteVal !== ALL_SITES) params.site = siteVal;
    const statsParams = siteVal !== ALL_SITES ? { site: siteVal } : {};
    const [c, s] = await Promise.all([
      api.get("/contractors", { params }),
      api.get("/contractors/stats/summary", { params: statsParams }),
    ]);
    setRows(c.data);
    setStats(s.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search, site), 300);
    return () => clearTimeout(t);
  }, [search, site]);

  const cards = [
    { label: "Contractors", value: stats.contractors, icon: Users, color: "#00355e", filter: "all" },
    { label: "Inducted", value: stats.inducted, icon: BadgeCheck, color: "#34C759", filter: "inducted" },
    { label: "Induction Expired", value: stats.induction_expired, icon: CalendarX, color: "#FF3B30", filter: "expired" },
    { label: "Active Permits", value: stats.permits_active, icon: FileCheck2, color: "#007AFF", filter: "active_permits" },
  ];

  const hasActive = (c) => (c.active_permit_types || []).length > 0;
  const visibleRows = rows.filter((c) => {
    if (filter === "inducted") return anyActiveInduction(c);
    if (filter === "expired") return !anyActiveInduction(c);
    if (filter === "active_permits") return hasActive(c);
    return true;
  });

  const filters = [
    { key: "all", label: "All" },
    { key: "inducted", label: "Inducted" },
    { key: "expired", label: "Induction expired" },
    { key: "active_permits", label: "With active permits" },
  ];

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">GFM - Permit to Work Management System</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Contractors</h1>
          <p className="text-sm text-muted-foreground">Site induction lasts 3 years. Issue work permits from each contractor's profile.</p>
        </div>
        <div className="flex gap-2">
          {showSiteFilter && (
            <Select value={site} onValueChange={setSite}>
              <SelectTrigger className="w-56" data-testid="dashboard-site-filter">
                <span className="flex items-center gap-2 truncate"><MapPin className="h-4 w-4 text-brand" /><SelectValue placeholder="All sites" /></span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SITES}>All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button className="bg-brand hover:bg-brand-dark" onClick={() => navigate("/contractors/new")} data-testid="add-contractor-button">
            <UserPlus className="mr-2 h-4 w-4" /> Add Contractor
          </Button>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const on = filter === c.filter;
          return (
            <button key={c.label} onClick={() => setFilter(c.filter)} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-md border bg-white p-5 text-left shadow-sm transition-colors hover:border-brand/50 ${on ? "border-brand ring-1 ring-brand" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</span>
                <Icon className="h-4 w-4" style={{ color: c.color }} />
              </div>
              <div className="mt-2 font-heading text-3xl font-bold tracking-tight" style={{ color: c.color }}>{c.value}</div>
            </button>
          );
        })}
      </div>

      <div className="rounded-md border border-border bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-border p-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="contractor-search"
            className="border-0 shadow-none focus-visible:ring-0"
            placeholder="Search by name, company, ID or reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="outline" size="sm" onClick={() => setGrouped((g) => !g)} data-testid="group-toggle-btn" className="whitespace-nowrap">
            {grouped ? <List className="mr-2 h-4 w-4" /> : <Building2 className="mr-2 h-4 w-4" />}
            {grouped ? "Flat list" : "Group by company"}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filter</span>
          {filters.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                filter === f.key ? "border-brand bg-brand text-white" : "border-border bg-white text-foreground hover:bg-secondary"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-muted-foreground">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="px-4 py-10 text-center text-muted-foreground" data-testid="empty-state">
            {rows.length === 0 ? "No contractors yet. Add one to run their site induction." : "No contractors match this filter."}
          </div>
        ) : grouped ? (
          <GroupedView rows={visibleRows} navigate={navigate} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left">
                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Ref</th>
                  <th className="px-4 py-3">Contractor</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Active Permits</th>
                  <th className="px-4 py-3">Site Inductions</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((c) => (
                  <ContractorRow key={c.id} c={c} navigate={navigate} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function PermitChips({ types }) {
  if (!types || types.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <span key={t} className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ background: permitMeta(t)?.color }} title={permitMeta(t)?.title}>
          {permitMeta(t)?.short}
        </span>
      ))}
    </div>
  );
}

function inductionState(c) {
  const inds = c.inductions || [];
  if (inds.length === 0) return "none";
  return inds.some((i) => !isExpired(i.induction_expiry)) ? "active" : "expired";
}
function anyActiveInduction(c) {
  return inductionState(c) === "active";
}
function StatusPill({ c, className = "" }) {
  const s = inductionState(c);
  if (s === "active") return <span className={`rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 ${className}`}>Inducted</span>;
  if (s === "none") return <span className={`rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-500 ${className}`}>Not inducted</span>;
  return <span className={`rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 ${className}`}>Expired</span>;
}

function ContractorRow({ c, navigate }) {
  const total = (c.inductions || []).length;
  const activeCount = (c.active_sites || []).length;
  return (
    <tr
      data-testid={`contractor-row-${c.id}`}
      onClick={() => navigate(`/contractors/${c.id}`)}
      className="cursor-pointer border-t border-border transition-colors hover:bg-secondary/50"
    >
      <td className="px-4 py-3 font-semibold text-brand">{c.reference}</td>
      <td className="px-4 py-3 font-medium">{c.name}</td>
      <td className="px-4 py-3 text-muted-foreground">{c.company}</td>
      <td className="px-4 py-3" data-testid={`active-permits-${c.id}`}><PermitChips types={c.active_permit_types} /></td>
      <td className="px-4 py-3 text-muted-foreground">{activeCount}/{total} site{total === 1 ? "" : "s"}</td>
      <td className="px-4 py-3"><StatusPill c={c} /></td>
    </tr>
  );
}

function GroupedView({ rows, navigate }) {
  const groups = {};
  rows.forEach((c) => {
    const key = c.company || "—";
    (groups[key] = groups[key] || []).push(c);
  });
  const companies = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  return (
    <div className="divide-y divide-border">
      {companies.map((company) => {
        const members = groups[company];
        const onSite = members.filter((m) => anyActiveInduction(m)).length;
        const expiredCount = members.length - onSite;
        return (
          <div key={company} data-testid={`company-group-${company}`}>
            <div className="flex items-center justify-between bg-brand/5 px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-brand" />
                <span className="font-heading font-bold tracking-tight text-brand">{company}</span>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-muted-foreground">{members.length}</span>
              </div>
              <div className="flex items-center gap-3 text-xs font-medium">
                <span className="text-green-700">{onSite} inducted</span>
                {expiredCount > 0 && <span className="text-red-700">{expiredCount} expired</span>}
              </div>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {members.map((c) => (
                  <tr key={c.id} data-testid={`contractor-row-${c.id}`} onClick={() => navigate(`/contractors/${c.id}`)}
                    className="cursor-pointer border-t border-border transition-colors hover:bg-secondary/50">
                    <td className="px-4 py-3 font-semibold text-brand">{c.reference}</td>
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3"><PermitChips types={c.active_permit_types} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{(c.active_sites || []).length}/{(c.inductions || []).length} sites</td>
                    <td className="px-4 py-3 text-right"><StatusPill c={c} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
